/** Create/edit a policy — the whole form is a field descriptor list over the shared schema. */

import * as React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import {
  calculatePeriodEnd,
  createPolicySchema,
  resolveCommissionRate,
  updatePolicySchema,
  type CreatePolicyInput,
  type PolicyDto,
  type RenewalFrequency,
  type VehicleCategory,
  today,
} from '@ias/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AutoForm, type FieldDescriptor } from '@/components/form/auto-form';
import { clientsApi, policiesApi, useAssignableUsers, useInsuranceCompanies } from '@/lib/api/domains';
import { useI18n } from '@/lib/i18n';

/**
 * Fills the end date in from start date + frequency, and then gets out of the way.
 *
 * This replaced a read-only preview printed under the form. The server has always derived a
 * blank end date, so the value was never wrong — but the operator could not see it in the
 * field, could not sanity-check it against the carrier's paperwork, and had to clear the
 * whole field to find out what the default even was. An annual policy starting 14/08 ends
 * 13/08 the next year; that belongs in the box, editable.
 *
 * The handover rule is the delicate part, and `dirtyFields` carries it:
 *
 *   • fill only once the operator has actually changed start date or frequency, so opening an
 *     existing policy never rewrites a date a carrier issued;
 *   • stop the moment they type in the end date themselves — from then on it is theirs, and a
 *     later frequency change must not silently overwrite it;
 *   • write with `shouldDirty: false`, otherwise this component's own write would look like a
 *     manual edit and it would immediately stop filling.
 *
 * One-off policies are skipped: `calculatePeriodEnd` returns null for them by design (spec
 * §6.4 requires an explicit end), and the schema already refuses to save without one.
 */
function DerivedEndDate({ form }: { form: UseFormReturn<Record<string, unknown>> }) {
  const start = form.watch('startDate') as string | undefined;
  const frequency = form.watch('renewalFrequency') as RenewalFrequency | undefined;
  const { dirtyFields } = form.formState;
  const endTouched = Boolean(dirtyFields.endDate);
  const periodTouched = Boolean(dirtyFields.startDate || dirtyFields.renewalFrequency);

  React.useEffect(() => {
    if (endTouched || !periodTouched || !start || !frequency) return;
    const derived = calculatePeriodEnd(start, frequency);
    if (derived) form.setValue('endDate', derived, { shouldDirty: false, shouldValidate: false });
  }, [start, frequency, endTouched, periodTouched, form]);

  return null;
}

/**
 * Fills the commission percentage in from the carrier's rate, and then gets out of the way.
 *
 * Every carrier pays the agency a standing rate, and it already lives on the carrier record.
 * Until now that number was shown on the insurer page and nowhere else: the operator retyped it
 * into every single policy, from memory, on a required field. One mistyped digit is a wrong
 * commission figure that nothing downstream can catch.
 *
 * Since migration 0010 the rate depends on the VEHICLE CATEGORY as well as the carrier — ΙΧ
 * επιβατικό, ΕΙΧ ενοικιαζόμενο, ΦΙΧ and ΜΟΤΟ are priced separately by every insurer — so this
 * watches both fields and re-resolves when either moves. `resolveCommissionRate` is the shared
 * rule, falling back to the carrier's single default for a category with no rate of its own.
 *
 * The handover rule is the same as `DerivedEndDate`, and for the same reasons:
 *
 *   • fill only once the operator has actually picked a carrier or a category, so opening an
 *     existing policy never rewrites the rate that was agreed when it was written — rates move
 *     over the years and a 2019 policy must keep its 2019 commission;
 *   • stop the moment they type in the percentage themselves, so an override survives a later
 *     change of carrier;
 *   • write with `shouldDirty: false`, or this component's own write would read as a manual edit
 *     and it would immediately stop filling.
 *
 * Carriers with no rate at all are skipped rather than zeroed: a blank field is an obvious
 * omission, whereas a confident 0% silently produces commission amounts of nothing.
 */
function DerivedCommission({
  form,
  insurers,
}: {
  form: UseFormReturn<Record<string, unknown>>;
  insurers: ReturnType<typeof useInsuranceCompanies>['data'];
}) {
  const companyId = form.watch('insuranceCompanyId') as string | undefined;
  const vehicleCategory = form.watch('vehicleCategory') as VehicleCategory | undefined;
  const { dirtyFields } = form.formState;
  const commissionTouched = Boolean(dirtyFields.commissionPercentage);
  const sourceTouched = Boolean(dirtyFields.insuranceCompanyId || dirtyFields.vehicleCategory);

  React.useEffect(() => {
    if (commissionTouched || !sourceTouched || !companyId) return;
    const rate = resolveCommissionRate(
      insurers?.find((insurer) => insurer.id === companyId),
      vehicleCategory || null,
    );
    if (rate === null || rate === '') return;
    form.setValue('commissionPercentage', rate, { shouldDirty: false, shouldValidate: false });
  }, [companyId, vehicleCategory, commissionTouched, sourceTouched, insurers, form]);

  return null;
}

export function PolicyFormDialog({
  open,
  onOpenChange,
  policy,
  clientId,
  defaultType,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policy?: PolicyDto;
  /** Pre-selects the client when opened from a client's page (spec §8.6). */
  clientId?: string;
  /** Pre-selects the product line — used by the cross-sell chips on the client card. */
  defaultType?: string;
}) {
  const { t } = useI18n();
  const { data: insurers } = useInsuranceCompanies();
  const { data: agents } = useAssignableUsers();
  const { data: clients } = clientsApi.useList({ perPage: 200, sortBy: 'fullName' });

  /**
   * The client this dialog was opened *for*, fetched by id.
   *
   * The list above is one page of 200 names and the book is several times that, so a client
   * outside that page had no option to match the pre-selected id and the field rendered empty.
   * Opened from a client's own page it is also locked — which left «Πελάτης» blank and
   * unfixable on the one screen that already knows the answer. Folded into the options below,
   * so whatever is selected is always among them.
   */
  const selectedClientId = policy?.clientId ?? clientId;
  const { data: selectedClient } = clientsApi.useDetail(selectedClientId);

  const clientOptions = React.useMemo(() => {
    const options = (clients?.data ?? []).map((client) => ({
      value: client.id,
      label: client.fullName,
    }));
    if (selectedClient && !options.some((option) => option.value === selectedClient.id)) {
      options.unshift({ value: selectedClient.id, label: selectedClient.fullName });
    }
    return options;
  }, [clients, selectedClient]);

  const create = policiesApi.useCreate<CreatePolicyInput>();
  const update = policiesApi.useUpdate<Partial<CreatePolicyInput>>();

  const isEdit = Boolean(policy);
  const formId = 'policy-form';

  const defaultValues = React.useMemo(
    () => ({
      clientId: policy?.clientId ?? clientId ?? '',
      insuranceCompanyId: policy?.insuranceCompanyId ?? '',
      insuranceType: policy?.insuranceType ?? defaultType ?? 'auto',
      policyNumber: policy?.policyNumber ?? '',
      renewalNumber: policy?.renewalNumber ?? '',
      startDate: policy?.startDate ?? today(),
      endDate: policy?.endDate ?? '',
      renewalFrequency: policy?.renewalFrequency ?? 'annual',
      premiumAmount: policy?.premiumAmount ?? '',
      netPremiumAmount: policy?.netPremiumAmount ?? '',
      vehicleCategory: policy?.vehicleCategory ?? '',
      commissionPercentage: policy?.commissionPercentage ?? '',
      coverageDescription: policy?.coverageDescription ?? '',
      status: policy?.status ?? 'active',
      paymentStatus: policy?.paymentStatus ?? 'unpaid',
      installmentCount: policy?.installmentCount ?? 1,
      renewalLeadDays: policy?.renewalLeadDays ?? 30,
      assignedAgentId: policy?.assignedAgentId ?? '',
      notes: policy?.notes ?? '',
      insuredItems: [],
    }),
    [policy, clientId, defaultType],
  );

  const fields: FieldDescriptor<Record<string, unknown>>[] = [
    {
      name: 'clientId',
      type: 'select',
      label: t('client.one'),
      required: true,
      disabled: Boolean(clientId),
      options: clientOptions,
    },
    {
      name: 'insuranceCompanyId',
      type: 'select',
      label: t('policy.company'),
      required: true,
      options: (insurers ?? []).map((insurer) => ({ value: insurer.id, label: insurer.name })),
    },
    { name: 'policyNumber', type: 'text', label: t('policy.number'), required: true },
    /*
     * Not required, and sitting directly under the central number because that is the pair
     * as it appears on the paperwork. Insurers that reuse the central number issue no
     * ανανεωτήριο at all, so demanding one would block half the book — the hint says so
     * rather than leaving an empty field looking like an omission.
     */
    {
      name: 'renewalNumber',
      type: 'text',
      label: t('policy.renewalNumber'),
      hint: t('policy.renewalNumberHint'),
    },
    { name: 'insuranceType', type: 'enum', enum: 'insuranceType', label: t('policy.insuranceType'), required: true },
    { name: 'renewalFrequency', type: 'enum', enum: 'frequency', label: t('policy.frequency'), required: true },
    {
      name: 'startDate',
      type: 'date',
      label: t('policy.startDate'),
      required: true,
    },
    {
      name: 'endDate',
      type: 'date',
      label: t('policy.endDate'),
      hint: t('policy.renewalPreview'),
    },
    /*
     * Ολικά first, καθαρά second, in that order because that is how they read off the carrier's
     * table and because the first is the one the client asks about. The hints carry the whole
     * distinction: one is what is paid, the other is what is earned on.
     */
    { name: 'premiumAmount', type: 'money', label: t('policy.premium'), hint: t('policy.premiumHint'), required: true },
    {
      name: 'netPremiumAmount',
      type: 'money',
      label: t('policy.netPremium'),
      hint: t('policy.netPremiumHint'),
    },
    /*
     * Sits directly above the percentage it decides. Not marked required: the book has 4.894
     * policies with no category yet, and a required field would block every edit of them.
     */
    {
      name: 'vehicleCategory',
      type: 'enum',
      enum: 'vehicleCategory',
      label: t('policy.vehicleCategory'),
      hint: t('policy.vehicleCategoryHint'),
    },
    { name: 'commissionPercentage', type: 'percent', label: t('policy.commissionPct'), required: true },
    { name: 'installmentCount', type: 'number', label: t('policy.installments') },
    { name: 'status', type: 'enum', enum: 'policyStatus', label: t('policy.status') },
    { name: 'paymentStatus', type: 'enum', enum: 'paymentStatus', label: t('policy.paymentStatus') },
    {
      name: 'assignedAgentId',
      type: 'select',
      label: t('client.agent'),
      options: (agents ?? []).map((agent) => ({ value: agent.id, label: agent.fullName })),
    },
    { name: 'coverageDescription', type: 'textarea', label: t('policy.coverage') },
    { name: 'notes', type: 'textarea', label: t('common.notes') },
  ];

  const submit = async (values: Record<string, unknown>) => {
    // The API derives the end date from frequency when it is blank; sending an empty
    // string instead of omitting the key would fail the ISO-date check.
    const payload = { ...values };
    if (!payload.endDate) delete payload.endDate;
    if (!payload.assignedAgentId) delete payload.assignedAgentId;
    /*
     * Cleared optionals cross as an explicit null, never as absent.
     *
     * The resolver turns an emptied input into `undefined` before validating, and `undefined`
     * does not survive JSON — so leaving it would mean an operator who clears a wrong καθαρά
     * figure silently keeps the old one. Null is the only way to say «δεν το ξέρω», and for the
     * καθαρά that is a real answer: it is what puts the policy back on the list of the ones
     * still waiting for them.
     */
    payload.netPremiumAmount = payload.netPremiumAmount || null;
    payload.vehicleCategory = payload.vehicleCategory || null;

    if (isEdit && policy) {
      await update.mutateAsync({ id: policy.id, input: payload as Partial<CreatePolicyInput> });
      toast.success(t('common.saved'));
    } else {
      const createdPolicy = await create.mutateAsync(payload as CreatePolicyInput);
      toast.success(`${t('policy.one')} ${createdPolicy.policyNumber}`);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('action.edit') : t('action.newPolicy')}</DialogTitle>
          <DialogDescription>
            {isEdit ? policy?.policyNumber : t('dialog.newPolicyHint')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <AutoForm
            id={formId}
            schema={isEdit ? updatePolicySchema : createPolicySchema}
            defaultValues={defaultValues as never}
            fields={fields}
            onSubmit={submit}
          >
            {(form) => (
              <>
                <DerivedEndDate form={form} />
                <DerivedCommission form={form} insurers={insurers} />
              </>
            )}
          </AutoForm>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button type="submit" form={formId} loading={create.isPending || update.isPending}>
            {t('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
